// Change password. The API bumps users.auth_generation, killing every OTHER
// session, then re-issues cookies for THIS one — so a successful change must
// NOT bounce the user to sign-in. That is what the copy promises and what the
// endpoint actually does.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Button, Field, Input } from '@fajrarabi/ui';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { AccountSection } from './AccountSection';

import { changePassword } from '~/lib/account';
import { ApiError } from '~/lib/api';

export function ChangePasswordCard() {
  const t = useT();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setRepeat('');
      setDone(true);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setDone(false);
    // Client-side only: the API has no idea what the user typed twice.
    if (next !== repeat) {
      setLocalError(t('app.account.password_repeat_mismatch'));
      return;
    }
    setLocalError(null);
    mutation.mutate();
  }

  const serverError = mutation.error;
  const errorMessage = localError ?? (serverError ? messageFor(serverError, t) : null);
  const busy = mutation.isPending;

  return (
    <AccountSection
      id="account-password"
      title={t('app.account.password_title')}
      description={t('app.account.password_description')}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t('app.account.password_current_label')}>
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              value={current}
              required
              onChange={(e) => setCurrent(e.target.value)}
            />
          )}
        </Field>

        <Field label={t('app.account.password_label')} hint={t('app.auth.password_hint_parent')}>
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={next}
              required
              onChange={(e) => setNext(e.target.value)}
            />
          )}
        </Field>

        <Field label={t('app.account.password_repeat_label')} error={localError ?? undefined}>
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={repeat}
              required
              onChange={(e) => setRepeat(e.target.value)}
            />
          )}
        </Field>

        {/* Server errors only — the repeat mismatch is already inline above. */}
        {!localError && errorMessage && <Alert tone="danger">{errorMessage}</Alert>}
        {done && <Alert tone="success">{t('app.account.password_success')}</Alert>}

        <div className="flex justify-end">
          <Button type="submit" disabled={busy}>
            {t('app.account.password_submit')}
          </Button>
        </div>
      </form>
    </AccountSection>
  );
}

/**
 * Map the API's error codes onto our own copy. The endpoint's messages are
 * English-only developer strings; a parent must never see one.
 */
function messageFor(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return t('app.account.password_error_wrong');
    if (err.code === 'invalid_input') return t('app.account.password_error_same');
  }
  return t('app.common.error_generic');
}
